/* ============================================================
   product-configurator-v2.js  — Garmin Configurator V2
   ============================================================ */
(function () {
  'use strict';

  const API_BASE            = 'https://movemore-recommendation-system.konto-agencyjne.workers.dev';
  const API_FILTERS         = `${API_BASE}/filters/options`;
  const API_RECOMMENDATIONS = `${API_BASE}/recommendations`;
  const SECTION_RENDERER    = 'assistant-card-renderer';
  const CARDS_INITIAL       = 4;   /* cards shown on first load */
  const CARDS_PER_PAGE      = 4;   /* cards added on "show more" */
  const PRICE_DEFAULT       = { min: 0, max: 6449 };

  /* Maps API battery_group label → POST enum value */
  const BATTERY_ENUM = {
    'Do 9 dni':        'do_9_dni',
    'Do 2 tygodni':    'do_2_tygodni',
    'Do miesiąca':     'do_miesiaca',
    'Energia solarna': 'solar'
  };

  /* ── Step sets ── */
  const BASIC_STEPS    = new Set(['price','case_size_group','battery','payments','connectivity']);
  const ADVANCED_STEPS = new Set(['sports','health_smart','screen_water']);

  /* Steps driven purely by toggles (no chip = "answered") */
  const TOGGLE_STEPS = new Set(['connectivity','health_smart','screen_water']);

  /* Toggle keys per merged step */
  const TOGGLE_STEP_KEYS = {
    connectivity: ['calls_bluetooth','calls_lte','multiband_gps','music'],
    health_smart: ['ecg','hrv','morning_report','evening_report','coach_running','coach_cycling','coach_strength','coach_fitness','recovery_time','maps_bool'],
    screen_water: ['display_type','screen_glass','water_resistance_atm','flashlight']
  };

  const TOGGLE_LABELS = {
    calls_bluetooth:'Rozmowy BT', calls_lte:'LTE', multiband_gps:'Multi-band GPS',
    music:'Muzyka', ecg:'EKG', hrv:'HRV', morning_report:'Poranny raport',
    evening_report:'Wieczorny raport', coach_running:'Running Coach',
    coach_cycling:'Cycling Coach', coach_strength:'Coach siłowy',
    coach_fitness:'Fitness Coach', recovery_time:'Regeneracja', maps_bool:'Mapy'
  };

  /* Sport group API key → display label */
  const SPORT_GROUPS = {
    bieganie:'Bieganie', rower:'Rower', plywanie:'Pływanie',
    silownia:'Siłownia / Fitness', sporty_zimowe:'Sporty zimowe',
    sporty_wodne:'Sporty wodne', sporty_rakietowe:'Sporty rakietowe',
    sporty_druzynowe:'Sporty drużynowe', sporty_walki:'Sporty walki',
    outdoor:'Outdoor / Rekreacja', multisport:'Multisport',
    motorowe:'Motorowe', wellness:'Wellness / Zdrowie'
  };

  /* ══════════════════════════════════════════════════════════
     SPORTS WIZARD — multi-round: main group → sub → add more?
  ══════════════════════════════════════════════════════════ */
  class SportsWizard {
    constructor(container, activityProfiles, sportsState, onChange, onRecordAnswer) {
      this._el             = container;
      this._profiles       = activityProfiles;
      this._sports         = sportsState;
      this._onChange       = onChange;
      this._onRecordAnswer = onRecordAnswer;
      this._expanded       = new Set();  /* which group rows are expanded */
      this._usedGroups     = new Set();  /* insertion order = primary first */
      this._render();
    }

    /* ── Single-screen accordion: all categories visible at once ── */
    _render() {
      this._el.innerHTML = '';

      const selectedCount = Object.keys(this._sports).length;

      /* ── Contextual hint — changes based on how many categories are selected ── */
      const hint = document.createElement('div');
      hint.className = 'pc2-sports-hint';

      if (selectedCount === 0) {
        hint.innerHTML = `
          <div class="pc2-sports-hint__step pc2-sports-hint__step--active">
            <span class="pc2-sports-hint__num">1</span>
            <div>
              <strong>Wybierz główny sport</strong>
              <span>Pierwsza wybrana kategoria będzie Twoją główną aktywnością</span>
            </div>
          </div>
          <div class="pc2-sports-hint__step pc2-sports-hint__step--dim">
            <span class="pc2-sports-hint__num">2</span>
            <div>
              <strong>Dodaj dodatkowe sporty</strong>
              <span>Opcjonalnie — zegarek dopasuje się do wszystkich Twoich aktywności</span>
            </div>
          </div>`;
      } else if (selectedCount === 1) {
        hint.innerHTML = `
          <div class="pc2-sports-hint__step pc2-sports-hint__step--done">
            <span class="pc2-sports-hint__num">✓</span>
            <div>
              <strong>Główny sport wybrany</strong>
            </div>
          </div>
          <div class="pc2-sports-hint__step pc2-sports-hint__step--active">
            <span class="pc2-sports-hint__num">2</span>
            <div>
              <strong>Chcesz dodać kolejne sporty?</strong>
              <span>Opcjonalnie — zaznacz dodatkowe kategorie poniżej</span>
            </div>
          </div>`;
      } else {
        hint.innerHTML = `
          <div class="pc2-sports-hint__step pc2-sports-hint__step--done">
            <span class="pc2-sports-hint__num">✓</span>
            <div>
              <strong>Wybrano ${selectedCount} kategorie sportowe</strong>
              <span>Możesz dodać więcej lub przejść dalej</span>
            </div>
          </div>`;
      }

      this._el.appendChild(hint);

      const list = document.createElement('div');
      list.className = 'pc2-sports-accordion';

      Object.entries(SPORT_GROUPS).forEach(([groupKey, groupLabel]) => {
        const activities = this._profiles[groupKey];
        if (!activities?.length) return;

        const isSelected = !!this._sports[groupKey];
        const isExpanded = this._expanded.has(groupKey);
        const isPrimary  = isSelected && Array.from(this._usedGroups)[0] === groupKey;

        /* ── Row wrapper ── */
        const row = document.createElement('div');
        row.className = 'pc2-sports-row' + (isSelected ? ' is-selected' : '');

        /* ── Header: checkbox + label + arrow ── */
        const header = document.createElement('div');
        header.className = 'pc2-sports-row__header';

        /* Checkbox area — click selects/deselects whole category */
        const checkWrap = document.createElement('label');
        checkWrap.className = 'pc2-sports-row__check';
        const cb = document.createElement('input');
        cb.type = 'checkbox';
        cb.checked = isSelected;
        cb.addEventListener('change', (e) => {
          e.stopPropagation();
          if (cb.checked) {
            if (!this._sports[groupKey]) this._sports[groupKey] = [...activities];
            if (!this._usedGroups.has(groupKey)) this._usedGroups.add(groupKey);
            this._expanded.add(groupKey);  /* auto-expand on select */
            row.classList.add('is-selected');
          } else {
            delete this._sports[groupKey];
            this._usedGroups.delete(groupKey);
            row.classList.remove('is-selected');
          }
          this._notifyChange();
          this._render();
        });
        const checkMark = document.createElement('span');
        checkMark.className = 'pc2-sports-row__checkmark';
        checkWrap.appendChild(cb);
        checkWrap.appendChild(checkMark);

        /* Label — clicking it toggles the checkbox */
        const labelEl = document.createElement('span');
        labelEl.className = 'pc2-sports-row__label';
        labelEl.textContent = groupLabel;
        labelEl.style.cursor = 'pointer';
        labelEl.addEventListener('click', () => cb.click());

        /* Primary badge */
        if (isPrimary) {
          const badge = document.createElement('span');
          badge.className = 'pc2-sports-row__primary-badge';
          badge.textContent = 'Główny';
          labelEl.appendChild(badge);
        }

        /* Sub-count when selected but collapsed */
        if (isSelected && !isExpanded) {
          const arr = this._sports[groupKey] || [];
          const count = document.createElement('span');
          count.className = 'pc2-sports-row__count';
          count.textContent = arr.length < activities.length
            ? `${arr.length} z ${activities.length}`
            : 'Wszystkie';
          labelEl.appendChild(count);
        }

        /* Arrow — click expands/collapses sub-activities */
        const arrow = document.createElement('button');
        arrow.type = 'button';
        arrow.className = 'pc2-sports-row__arrow' + (isExpanded ? ' is-open' : '');
        arrow.setAttribute('aria-label', isExpanded ? 'Zwiń' : 'Rozwiń aktywności');
        arrow.innerHTML = `${isExpanded ? 'Zwiń' : 'Rozwiń'}<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>`;
        arrow.addEventListener('click', (e) => {
          e.stopPropagation();
          if (this._expanded.has(groupKey)) this._expanded.delete(groupKey);
          else this._expanded.add(groupKey);
          this._render();
        });

        header.appendChild(checkWrap);
        header.appendChild(labelEl);
        header.appendChild(arrow);
        row.appendChild(header);

        /* ── Sub-activities (shown when expanded) ── */
        if (isExpanded) {
          const subsWrap = document.createElement('div');
          subsWrap.className = 'pc2-sports-row__subs';

          const arr = this._sports[groupKey] || [];

          /* "All / None" row */
          const actRow = document.createElement('div');
          actRow.className = 'pc2-sports-chip-actions';
          const allBtn = document.createElement('button');
          allBtn.type = 'button'; allBtn.className = 'pc2-sports-all-btn';
          allBtn.textContent = 'Zaznacz wszystkie';
          allBtn.addEventListener('click', () => {
            this._sports[groupKey] = [...activities];
            if (!this._usedGroups.has(groupKey)) this._usedGroups.add(groupKey);
            this._notifyChange();
            this._render();
          });
          const noneBtn = document.createElement('button');
          noneBtn.type = 'button'; noneBtn.className = 'pc2-sports-all-btn';
          noneBtn.textContent = 'Odznacz wszystkie';
          noneBtn.addEventListener('click', () => {
            this._sports[groupKey] = [];
            this._notifyChange();
            this._render();
          });
          actRow.appendChild(allBtn);
          actRow.appendChild(noneBtn);
          subsWrap.appendChild(actRow);

          const chips = document.createElement('div');
          chips.className = 'pc2-sport-chips pc2-sport-chips--sub';
          activities.forEach(activity => {
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'pc2-chip pc2-chip--sub' + (arr.includes(activity) ? ' is-active' : '');
            btn.textContent = activity;
            btn.addEventListener('click', () => {
              if (!this._sports[groupKey]) this._sports[groupKey] = [];
              if (!this._usedGroups.has(groupKey)) this._usedGroups.add(groupKey);
              const idx = this._sports[groupKey].indexOf(activity);
              if (idx > -1) { this._sports[groupKey].splice(idx, 1); btn.classList.remove('is-active'); }
              else           { this._sports[groupKey].push(activity);  btn.classList.add('is-active'); }
              /* If all deselected, remove group */
              if (!this._sports[groupKey].length) {
                delete this._sports[groupKey];
                this._usedGroups.delete(groupKey);
              }
              /* Mark category row as selected if any activity is picked */
              row.classList.toggle('is-selected', !!this._sports[groupKey]);
              this._notifyChange();
            });
            chips.appendChild(btn);
          });
          subsWrap.appendChild(chips);
          row.appendChild(subsWrap);
        }

        list.appendChild(row);
      });

      this._el.appendChild(list);
    }

    _notifyChange() {
      this._onChange();
      this._writeSportsAnswer();
    }

    _selectedLabels() {
      return Object.entries(this._sports).flatMap(([groupKey, subs]) =>
        subs?.length ? subs : [SPORT_GROUPS[groupKey] || groupKey]
      );
    }

    _writeSportsAnswer() {
      const labels = this._selectedLabels();
      console.log('[pc] SportsWizard _writeSportsAnswer:', labels.join(', ') || '(empty)');
      console.log('[pc] SportsWizard this._sports:', JSON.stringify(this._sports));
      this._onRecordAnswer('sports', labels.length ? labels.join(', ') : null);
    }

    reset() {
      Object.keys(this._sports).forEach(k => delete this._sports[k]);
      this._usedGroups = new Set();
      this._expanded   = new Set();
      this._render();
      this._writeSportsAnswer();
    }

    restoreState(savedSports) {
      this._usedGroups = new Set(Object.keys(savedSports));
      this._expanded   = new Set(Object.keys(savedSports));  /* restore expanded */
      this._render();
      this._writeSportsAnswer();
    }
  }


  /* ══════════════════════════════════════════════════════════
     MAIN ELEMENT
  ══════════════════════════════════════════════════════════ */
  class GarminKonfigurator extends HTMLElement {

    connectedCallback() {
      this._filterOptions  = null;
      this._cardCache      = {};
      this._debounce       = null;
      this._allHandles     = [];   /* full list from API */
      this._shownCount     = 0;   /* how many cards currently rendered */
      this._sportsWizard   = null;
      this._steps          = [];
      this._currentStep    = 0;
      this._answers        = {};
      this._editMode       = false;

      /* Bind welcome button immediately — before async _init so click works right away */
      this._bindWelcome();

      this._state = {
        priceMin:0, priceMax:6449,
        battery:[], case_size_groups:[], payments:null,
        calls_bluetooth:false, calls_lte:false, multiband_gps:false, music:false,
        water_resistance_atm:null, display_type:null, screen_glass:null, flashlight:null,
        ecg:false, hrv:false, morning_report:false, evening_report:false,
        coach_running:false, coach_cycling:false, coach_strength:false, coach_fitness:false,
        recovery_time:false, maps:null, maps_bool:false,
        sports:{}
      };

      this._init();
    }

    /* ── INIT ── */
    async _init() {
      try {
        const res = await fetch(API_FILTERS);
        if (!res.ok) throw new Error(res.status);
        this._filterOptions = await res.json();
      } catch(e) {
        console.warn('[pc] Filter API unavailable.', e);
        this._filterOptions = {
          basic:    { ranges:{price:PRICE_DEFAULT}, battery_groups:[], case_size_groups:[], connectivity:{}, features:{} },
          advanced: { water_resistance:[], display_types:[], screen_glass:[], activity_profiles:{} }
        };
      }

      /* Init price defaults from API — always start from 0, take max from API */
      const priceRange = this._filterOptions.basic?.ranges?.price || PRICE_DEFAULT;
      this._state.priceMin = 0;
      this._state.priceMax = priceRange.max || PRICE_DEFAULT.max;

      this._buildDynamicFilters();
      this._bindStaticFilters();
      this._initWizard();
      this._bindMisc();

      /* Restore previous session if available */
      const session = this._loadSession();
      if (session) {
        console.log('[pc] Restoring session from sessionStorage');
        /* Restore state — merge into existing object to keep sports reference intact */
        const { sports: savedSports, ...restState } = session.state;
        Object.assign(this._state, restState);
        /* Restore sports in-place */
        Object.keys(this._state.sports).forEach(k => delete this._state.sports[k]);
        Object.assign(this._state.sports, savedSports || {});
        /* Restore answers */
        this._answers = session.answers || {};
        /* Restore UI chips/checkboxes */
        this._restoreUI();
        /* Skip welcome card — user was already using the configurator */
        const welcome = this.querySelector('#pc-welcome');
        const wizard  = this.querySelector('#pc-wizard');
        if (welcome) welcome.setAttribute('hidden', '');
        if (wizard)  wizard.removeAttribute('hidden');
        /* If user was on results view, skip wizard and show results directly */
        if (session.inResults) {
          this._showResults();
        }
      }
    }

    /* ── BUILD DYNAMIC FILTERS ── */
    _buildDynamicFilters() {
      const basic    = this._filterOptions.basic    || {};
      const advanced = this._filterOptions.advanced || {};
      const price    = basic.ranges?.price || PRICE_DEFAULT;

      this._initPriceSlider(price);
      this._buildChips('#pc2-water',        (advanced.water_resistance||[]).map(w=>({label:w.label,value:w.value})), 'water_resistance_atm');
      this._buildChips('#pc2-display-type', (advanced.display_types||[]).map(d=>({label:d,value:d})),              'display_type');
      this._buildChips('#pc2-screen-glass', (advanced.screen_glass||[]).map(g=>({label:g,value:g})),               'screen_glass');

      console.log('[pc] _buildDynamicFilters: case_size_groups=', (basic.case_size_groups||[]).length, 'battery_groups=', (basic.battery_groups||[]).length, 'water_resistance=', (advanced.water_resistance||[]).length, 'display_types=', (advanced.display_types||[]).length, 'screen_glass=', (advanced.screen_glass||[]).length);
      /* Sports wizard — pass onRecordAnswer so sidebar updates without waiting for Next */
      const container = this.querySelector('#pc2-sports-wizard');
      if (container) {
        this._sportsWizard = new SportsWizard(
          container,
          advanced.activity_profiles || {},
          this._state.sports,
          () => this._onFilterChange(),
          (key, value) => {
            if (value) this._answers[key] = value;
            else delete this._answers[key];
          }
        );
        container.addEventListener('sports:done', () => {
          this._advanceOrFinish();
        });
      }
    }

    _buildChips(selector, items, stateKey) {
      const container = this.querySelector(selector);
      if (!container || !items.length) return;
      items.forEach(({ label, value }) => {
        const btn = document.createElement('button');
        btn.className = 'pc2-chip'; btn.type = 'button';
        btn.textContent = label; btn.dataset.filter = stateKey; btn.dataset.value = String(value);
        btn.addEventListener('click', () => {
          const same = String(this._state[stateKey]) === String(value);
          this._state[stateKey] = same ? null : value;
          container.querySelectorAll('.pc2-chip').forEach(b => b.classList.remove('is-active'));
          if (!same) btn.classList.add('is-active');
          this._updateNextBtn(); this._onFilterChange();
        });
        container.appendChild(btn);
      });
    }

    /* Multi-select chip builder — toggles values in/out of a state array */
    _buildChipsMulti(selector, items, stateKey) {
      const container = this.querySelector(selector);
      if (!container || !items.length) return;
      if (!Array.isArray(this._state[stateKey])) this._state[stateKey] = [];
      items.forEach(({ label, value }) => {
        const btn = document.createElement('button');
        btn.className = 'pc2-chip'; btn.type = 'button';
        btn.textContent = label; btn.dataset.filter = stateKey; btn.dataset.value = String(value);
        btn.addEventListener('click', () => {
          const arr = this._state[stateKey];
          const idx = arr.indexOf(value);
          if (idx > -1) { arr.splice(idx, 1); btn.classList.remove('is-active'); }
          else           { arr.push(value);    btn.classList.add('is-active'); }
          this._updateNextBtn(); this._onFilterChange();
        });
        container.appendChild(btn);
      });
    }

    _initPriceSlider({ min:_ignored, max:PMAX }) {
      const PMIN = 0; /* always start from 0 regardless of API */
      const sMin=this.querySelector('#pc2-price-smin'), sMax=this.querySelector('#pc2-price-smax');
      const iMin=this.querySelector('#pc2-price-imin'), iMax=this.querySelector('#pc2-price-imax');
      const fill=this.querySelector('#pc2-range-fill'), disp=this.querySelector('#pc2-price-display');
      if (!sMin) return;
      [sMin,sMax].forEach(el=>{el.min=PMIN;el.max=PMAX;});
      [iMin,iMax].forEach(el=>{el.min=PMIN;el.max=PMAX;});
      const pct=v=>((v-PMIN)/(PMAX-PMIN))*100, fmt=v=>v.toLocaleString('pl-PL');
      const sync=()=>{
        let lo=parseInt(sMin.value)||PMIN, hi=parseInt(sMax.value)||PMAX;
        lo=Math.max(PMIN,Math.min(lo,PMAX)); hi=Math.max(PMIN,Math.min(hi,PMAX));
        if(lo>hi)[lo,hi]=[hi,lo];
        sMin.value=lo;sMax.value=hi;iMin.value=lo;iMax.value=hi;
        this._state.priceMin=lo;this._state.priceMax=hi;
        if(fill){fill.style.left=pct(lo)+'%';fill.style.width=(pct(hi)-pct(lo))+'%';}
        if(disp)disp.textContent=`${fmt(lo)} – ${fmt(hi)} zł`;
      };
      sMin.value=PMIN;sMax.value=PMAX;iMin.value=PMIN;iMax.value=PMAX;sync();
      [sMin,sMax].forEach(el=>el.addEventListener('input',()=>{sync();this._updateNextBtn();this._onFilterChange();}));
      iMin.addEventListener('change',()=>{sMin.value=iMin.value;sync();this._updateNextBtn();this._onFilterChange();});
      iMax.addEventListener('change',()=>{sMax.value=iMax.value;sync();this._updateNextBtn();this._onFilterChange();});
    }

    /* ── BIND STATIC FILTERS ── */
    _bindStaticFilters() {
      const dynamic = '#pc2-case-size,#pc2-battery,#pc2-water,#pc2-display-type,#pc2-screen-glass,#pc2-sports-wizard';
      const allChips = this.querySelectorAll('.pc2-chip[data-filter]');
      const allCheckboxes = this.querySelectorAll('input[data-filter][type=checkbox]');
      console.log('[pc] _bindStaticFilters: found', allChips.length, 'chips,', allCheckboxes.length, 'checkboxes');
      console.log('[pc] checkbox ids:', Array.from(allCheckboxes).map(i=>i.id+':'+i.dataset.filter));
      this.querySelectorAll('.pc2-chip[data-filter]').forEach(btn => {
        if (btn.closest(dynamic)) return;
        btn.addEventListener('click', () => {
          const filter=btn.dataset.filter, raw=btn.dataset.value;
          const siblings=btn.closest('.pc-options')?.querySelectorAll(`.pc2-chip[data-filter="${filter}"]`);
          if (raw==='true'||raw==='false') {
            const bv=raw==='true', same=this._state[filter]===bv;
            this._state[filter]=same?null:bv;
            siblings?.forEach(b=>b.classList.remove('is-active'));
            if(!same)btn.classList.add('is-active');
          } else {
            const same=this._state[filter]===raw;
            this._state[filter]=same?null:raw;
            siblings?.forEach(b=>b.classList.remove('is-active'));
            if(!same)btn.classList.add('is-active');
          }
          console.log('[pc] chip click:', filter, '=', this._state[filter]);
          this._updateNextBtn();this._onFilterChange();
        });
      });
      /* Multi-value checkboxes (battery, case_size_groups) — data-filter-multi attribute */
      this.querySelectorAll('input[data-filter-multi][type=checkbox]').forEach(inp => {
        const stateKey = inp.dataset.filterMulti;
        const value    = inp.dataset.value;
        inp.addEventListener('change', () => {
          if (!Array.isArray(this._state[stateKey])) this._state[stateKey] = [];
          const arr = this._state[stateKey];
          if (inp.checked) { if (!arr.includes(value)) arr.push(value); }
          else { const i = arr.indexOf(value); if (i > -1) arr.splice(i, 1); }
          console.log('[pc] checkbox change (multi):', stateKey, '=', JSON.stringify(this._state[stateKey]));
          this._updateNextBtn(); this._onFilterChange();
        });
      });

      this.querySelectorAll('input[data-filter][type=checkbox]').forEach(inp => {
        inp.addEventListener('change', () => {
          const val = inp.checked;
          console.log('[pc] checkbox change:', inp.dataset.filter, '=', val);
          this._state[inp.dataset.filter] = val;
          /* maps_bool → also set maps state key */
          if (inp.dataset.filter === 'maps_bool') this._state.maps = val ? true : null;
          this._updateNextBtn();this._onFilterChange();
        });
      });
    }

    /* ── WIZARD ── */
    _initWizard() {
      /* All .pc-step elements in DOM order — checkpoint included */
      const allSteps = Array.from(this.querySelectorAll('.pc-step'));
      if (!allSteps.length) return;

      /* _steps = real steps (no checkpoint) — used for progress + answers */
      this._steps = allSteps.filter(s => s.dataset.step !== 'checkpoint');
      /* _track = every slide including checkpoint */
      this._track = allSteps;

      const track      = this.querySelector('#pc-steps-track');
      const bar        = this.querySelector('#pc-progress-bar');
      const label      = this.querySelector('#pc-step-label');
      const backBtn    = this.querySelector('#pc-back-btn');
      const nextBtn    = this.querySelector('#pc-next-btn');
      const skipBtn    = this.querySelector('#pc-skip-btn');
      const restartBtn = this.querySelector('#pc-restart-btn');

      const totalTrack = this._track.length;
      const totalSteps = this._steps.length;
      const checkpointIdx = this._track.findIndex(s => s.dataset.step === 'checkpoint');

      this._trackIdx = 0;

      const goTo = (idx) => {
        if (idx < 0 || idx >= totalTrack) return;
        this._trackIdx = idx;

        /* Slide the track */
        track.style.transform = `translateX(-${idx * 100}%)`;

        const step    = this._track[idx];
        const stepKey = step.dataset.step;
        const group   = step.dataset.group;

        /* ── CHECKPOINT slide ── */
        if (stepKey === 'checkpoint') {
          backBtn.disabled = false;
          nextBtn.classList.add('is-hidden');
          skipBtn.classList.add('is-hidden');
          label.innerHTML = '';
          bar.style.width = '100%';
          this.querySelector('#pc-progress').setAttribute('aria-valuenow', 100);
          return;
        }

        /* ── Normal step ── */
        const stepIdx = this._steps.indexOf(step);
        this._currentStep = stepIdx;

        bar.style.width = `${(stepIdx / totalSteps) * 100}%`;
        this.querySelector('#pc-progress')
          .setAttribute('aria-valuenow', Math.round((stepIdx / totalSteps) * 100));

        label.innerHTML = `
          <span class="pc-step-group-badge pc-step-group-badge--${group}">
            ${group === 'basic' ? 'Podstawowe' : 'Zaawansowane'}
          </span>
          ${step.dataset.label || ''}
          <span class="pc-step-counter">${stepIdx + 1}&nbsp;/&nbsp;${totalSteps}</span>`;

        backBtn.disabled = idx === 0 && !this._editMode;

        if (this._editMode) {
          nextBtn.textContent = 'Zastosuj';
          nextBtn.classList.remove('is-hidden');
          skipBtn.classList.add('is-hidden');
          return;
        }

        nextBtn.classList.remove('is-hidden');
        skipBtn.classList.add('is-hidden');
        nextBtn.textContent = idx === totalTrack - 1 ? 'Zobacz wyniki' : 'Dalej';
        nextBtn.classList.add('pc-btn--primary');
        nextBtn.classList.remove('pc-btn--ghost', 'pc-btn--sm');
        this._updateNextBtn();
      };

      /* ── Nav buttons ── */
      backBtn.addEventListener('click', () => {
        if (this._editMode) { this._exitEditMode(false); return; }
        goTo(this._trackIdx - 1);
      });

      nextBtn.addEventListener('click', () => {
        this._recordAnswer(this._track[this._trackIdx]);
        if (this._editMode) { this._exitEditMode(true); return; }
        if (this._trackIdx + 1 >= totalTrack) { this._showResults(); return; }
        goTo(this._trackIdx + 1);
      });

      skipBtn.addEventListener('click', () => {
        delete this._answers[this._track[this._trackIdx]?.dataset.step];
        goTo(this._trackIdx + 1);
      });

      /* ── Checkpoint buttons ── */
      this.querySelector('#pc-show-results-btn')?.addEventListener('click', () => {
        this._showResults();
      });
      this.querySelector('#pc-checkpoint-continue')?.addEventListener('click', () => {
        goTo(checkpointIdx + 1);
      });

      restartBtn.addEventListener('click', () => this._restartWizard());

      /* _goToFn: called from edit mode with a real step index */
      this._goToFn = (stepIdx) => {
        const step = this._steps[stepIdx];
        const trackIdx = this._track.indexOf(step);
        goTo(trackIdx >= 0 ? trackIdx : stepIdx);
      };

      goTo(0);
    }

    _updateNextBtn() {
      const btn=this.querySelector('#pc-next-btn'); if(!btn)return;
      const step=this._steps[this._currentStep]; if(!step)return;
      btn.classList.toggle('has-selection',this._stepHasSelection(step));
    }

    _stepHasSelection(step) {
      const key=step.dataset.step;
      if(key==='price'){const p=this._filterOptions?.basic?.ranges?.price||PRICE_DEFAULT;return this._state.priceMin!==0||this._state.priceMax!==p.max;}
      if(key==='sports')return Object.keys(this._state.sports).length>0;
      const keys=TOGGLE_STEP_KEYS[key]||[];
      if(keys.length)return keys.some(k=>{
        const v=this._state[k];
        return v!==null&&v!==false&&v!==undefined;
      });
      const stateKey=step.querySelector('.pc2-chip[data-filter]')?.dataset.filter;
      if (!stateKey) return false;
      const sv = this._state[stateKey];
      return Array.isArray(sv) ? sv.length > 0 : sv !== null;
    }

    _recordAnswer(step) {
      const key=step.dataset.step; if(!key)return;

      /* Toggle-based merged steps — handle both booleans (toggles) and strings (single-select chips) */
      const keys=TOGGLE_STEP_KEYS[key]||[];
      if(keys.length) {
        const active=keys
          .map(k=>({ k, v:this._state[k] }))
          .filter(({ v })=> v === true || (v !== null && v !== false && v !== undefined && v !== ''))
          .map(({ k, v })=> v === true ? (TOGGLE_LABELS[k]||k) : String(v));
        if(active.length)this._answers[key]=active.join(', ');
        else delete this._answers[key];
        return;
      }
      if(key==='price'){
        const p=this._filterOptions?.basic?.ranges?.price||PRICE_DEFAULT;
        const unchanged=this._state.priceMin===0&&this._state.priceMax===p.max;
        if(!unchanged)this._answers[key]=`${this._state.priceMin.toLocaleString('pl-PL')} – ${this._state.priceMax.toLocaleString('pl-PL')} zł`;
        else delete this._answers[key];
        return;
      }
      if(key==='sports'){
        /* Use same label logic as SportsWizard: if no subs, show group name */
        const labels=Object.entries(this._state.sports).flatMap(([gk,subs])=>
          subs.length ? subs : [SPORT_GROUPS[gk]||gk]
        );
        if(labels.length)this._answers[key]=labels.join(', ');
        else delete this._answers[key];
        return;
      }
      /* data-filter-multi checkboxes (battery, case_size_groups) — read from state */
      const firstMulti = step.querySelector('input[data-filter-multi]');
      if (firstMulti) {
        const stateKey = firstMulti.dataset.filterMulti;
        const arr = this._state[stateKey];
        if (Array.isArray(arr) && arr.length) {
          /* Map battery values back to display labels via checked checkboxes */
          const labels = Array.from(step.querySelectorAll('input[data-filter-multi]:checked'))
            .map(inp => inp.closest('.pc-feature-card')?.querySelector('.pc-feature-label')?.childNodes[0]?.textContent?.trim())
            .filter(Boolean);
          this._answers[key] = labels.length ? labels.join(', ') : arr.join(', ');
        } else {
          delete this._answers[key];
        }
        return;
      }
      /* data-filter toggles (connectivity, health_smart etc.) — already handled above */
      /* Single-select chips (payments, flashlight) */
      const firstChip = step.querySelector('.pc2-chip[data-filter]');
      if (firstChip) {
        const stateKey = firstChip.dataset.filter;
        const sv = this._state[stateKey];
        if (Array.isArray(sv)) {
          const activeChips = Array.from(step.querySelectorAll('.pc2-chip.is-active')).map(b => b.textContent.trim());
          if (activeChips.length) this._answers[key] = activeChips.join(', ');
          else delete this._answers[key];
          return;
        }
      }
      const activeChip=step.querySelector('.pc2-chip.is-active');
      if(activeChip)this._answers[key]=activeChip.textContent.trim();
      else delete this._answers[key];
    }

    _advanceOrFinish() {
      const nextTrack = this._trackIdx + 1;
      if (nextTrack < (this._track || this._steps).length) {
        if (this._goToTrackFn) this._goToTrackFn(nextTrack);
        else this._goToFn(this._currentStep + 1);
      } else {
        this._showResults();
      }
    }

    /* ── EDIT MODE ── */
    _enterEditMode(idx) {
      this._editMode=true;
      this.querySelector('#pc-results-view').setAttribute('hidden','');
      this.querySelector('#pc-wizard').style.display='';
      const bb=this.querySelector('#pc-back-btn');
      bb.disabled=false;
      bb.innerHTML=`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg> Anuluj`;
      this._goToFn(idx);
    }

    _exitEditMode(apply) {
      this._editMode=false;
      this.querySelector('#pc-wizard').style.display='none';
      this.querySelector('#pc-results-view').removeAttribute('hidden');
      this.querySelector('#pc-back-btn').innerHTML=`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg> Wstecz`;
      if(apply){this._renderSummary();this._saveSession();this._fetchRecommendations();}
    }

    /* ── SHOW RESULTS ── */
    _showResults() {
      console.log('[pc] _showResults — full state:', JSON.stringify(this._state));
      /* Record answers for all steps before showing summary — checkboxes update
         state directly but _recordAnswer is only called on Next click */
      (this._steps || []).forEach(step => this._recordAnswer(step));
      this.querySelector('#pc-wizard').style.display='none';
      this.querySelector('#pc-results-view').removeAttribute('hidden');
      this._renderSummary();
      this._saveSession();
      this._fetchRecommendations();
      /* On desktop the summary is always visible via CSS.
         On mobile: open it once on first show so user sees their choices */
      if (window.innerWidth >= 1024) {
        this.querySelector('.pc-summary')?.classList.add('is-open');
      }
    }

    _renderSummary() {
      const list=this.querySelector('#pc-summary-list'); if(!list)return;
      list.innerHTML=''; let advAdded=false;
      this._steps.forEach((step,idx)=>{
        const key=step.dataset.step,group=step.dataset.group,val=this._answers[key];
        if(group==='advanced'&&!advAdded){
          const h=document.createElement('li');h.className='pc-summary-group-header';h.textContent='Zaawansowane';
          list.appendChild(h);advAdded=true;
        } else if(group==='basic'&&idx===0){
          const h=document.createElement('li');h.className='pc-summary-group-header';h.textContent='Podstawowe';
          list.appendChild(h);
        }
        const li=document.createElement('li');li.className='pc-summary-item';
        li.innerHTML=`<div class="pc-summary-item-body"><span class="pc-summary-item-label">${step.dataset.label||key}</span><span class="pc-summary-item-value${val?'':' is-skipped'}">${val||'Pominięto'}</span></div><svg class="pc-summary-edit-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>`;
        li.addEventListener('click',()=>this._enterEditMode(idx));
        list.appendChild(li);
      });
    }

    _restartWizard() {
      this._editMode=false;
      this.querySelector('#pc-results-view').setAttribute('hidden','');
      this.querySelector('#pc-wizard').style.display='';
      this._answers={};
      this.querySelectorAll('.pc2-chip.is-active').forEach(c=>c.classList.remove('is-active'));
      this.querySelectorAll('input[type=checkbox], input[data-filter-multi]').forEach(i=>{i.checked=false;});
      const p=this._filterOptions?.basic?.ranges?.price||PRICE_DEFAULT;
      /* Clear sports in-place so SportsWizard shared reference stays valid */
      Object.keys(this._state.sports).forEach(k => delete this._state.sports[k]);
      Object.assign(this._state,{
        priceMin:0,priceMax:p.max,battery:[],case_size_groups:[],payments:null,
        calls_bluetooth:false,calls_lte:false,multiband_gps:false,music:false,
        water_resistance_atm:null,display_type:null,screen_glass:null,flashlight:null,
        ecg:false,hrv:false,morning_report:false,evening_report:false,
        coach_running:false,coach_cycling:false,coach_strength:false,coach_fitness:false,
        recovery_time:false,maps:null,maps_bool:false
        /* sports not replaced here — cleared above in-place */
      });
      this._resetPriceSlider(p);
      if(this._sportsWizard)this._sportsWizard.reset();
      this.querySelector('#pc-back-btn').innerHTML=`<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg> Wstecz`;
      this._clearSession();
      this._goToFn(0);
    }

    /* ── SESSION PERSISTENCE ── */
    _saveSession() {
      try {
        const session = {
          state:   this._state,
          answers: this._answers,
          inResults: !this.querySelector('#pc-results-view').hasAttribute('hidden')
        };
        sessionStorage.setItem('pc_garmin_v2', JSON.stringify(session));
      } catch(e) { /* sessionStorage unavailable (private mode etc.) */ }
    }

    _loadSession() {
      try {
        const raw = sessionStorage.getItem('pc_garmin_v2');
        if (!raw) return null;
        return JSON.parse(raw);
      } catch(e) { return null; }
    }

    _clearSession() {
      try { sessionStorage.removeItem('pc_garmin_v2'); } catch(e) {}
    }

    /* Restore UI chip/checkbox state from this._state after a session restore */
    _restoreUI() {
      const s = this._state;

      /* Price slider */
      const p = this._filterOptions?.basic?.ranges?.price || PRICE_DEFAULT;
      if (s.priceMin !== p.min || s.priceMax !== p.max) {
        const sMin = this.querySelector('#pc2-price-smin');
        const sMax = this.querySelector('#pc2-price-smax');
        const iMin = this.querySelector('#pc2-price-imin');
        const iMax = this.querySelector('#pc2-price-imax');
        const fill = this.querySelector('#pc2-range-fill');
        const disp = this.querySelector('#pc2-price-display');
        const pct = v => ((v - p.min) / (p.max - p.min)) * 100;
        const fmt = v => v.toLocaleString('pl-PL');
        if (sMin) { sMin.value = s.priceMin; sMax.value = s.priceMax; }
        if (iMin) { iMin.value = s.priceMin; iMax.value = s.priceMax; }
        if (fill) { fill.style.left = pct(s.priceMin) + '%'; fill.style.width = (pct(s.priceMax) - pct(s.priceMin)) + '%'; }
        if (disp) disp.textContent = `${fmt(s.priceMin)} – ${fmt(s.priceMax)} zł`;
      }

      /* Multi-value checkboxes (battery, case_size_groups) */
      this.querySelectorAll('input[data-filter-multi][type=checkbox]').forEach(inp => {
        const arr = this._state[inp.dataset.filterMulti];
        if (Array.isArray(arr) && arr.includes(inp.dataset.value)) inp.checked = true;
      });

      /* Single-select chips (display_type, screen_glass, water) */
      const chipStateMap = {
        display_type:         '#pc2-display-type',
        screen_glass:         '#pc2-screen-glass',
        water_resistance_atm: '#pc2-water'
      };
      Object.entries(chipStateMap).forEach(([stateKey, selector]) => {
        const val = s[stateKey];
        if (val === null || val === undefined) return;
        const container = this.querySelector(selector);
        container?.querySelectorAll('.pc2-chip').forEach(btn => {
          if (String(btn.dataset.value) === String(val)) btn.classList.add('is-active');
        });
      });

      /* Static boolean chips (payments, flashlight, maps) */
      ['payments', 'flashlight'].forEach(key => {
        const val = s[key];
        if (val === null) return;
        this.querySelectorAll(`.pc2-chip[data-filter="${key}"]`).forEach(btn => {
          const bv = btn.dataset.value === 'true';
          if (bv === val) btn.classList.add('is-active');
        });
      });

      /* Checkboxes */
      const checkboxKeys = [
        'calls_bluetooth','calls_lte','multiband_gps','music',
        'ecg','hrv','morning_report','evening_report',
        'coach_running','coach_cycling','coach_strength','coach_fitness',
        'recovery_time','maps_bool'
      ];
      checkboxKeys.forEach(key => {
        if (!s[key]) return;
        const inp = this.querySelector(`input[data-filter="${key}"]`);
        if (inp) inp.checked = true;
      });

      /* Sports wizard — restore via its own reset+state method */
      if (this._sportsWizard && Object.keys(s.sports).length) {
        this._sportsWizard.restoreState(s.sports);
      }
    }

    /* ── WELCOME CARD — bound immediately before async init ── */
    _bindWelcome() {
      const welcome    = this.querySelector('#pc-welcome');
      const wizard     = this.querySelector('#pc-wizard');
      const welcomeBtn = this.querySelector('#pc-welcome-btn');
      if (!welcomeBtn) return;
      welcomeBtn.addEventListener('click', () => {
        if (welcome) welcome.setAttribute('hidden', '');
        if (wizard)  wizard.removeAttribute('hidden');
      });
    }

    /* ── MISC ── */
    _bindMisc() {
      this.querySelector('#pc-restart-btn-2')?.addEventListener('click',()=>this._restartWizard());
      this.querySelector('#pc-load-more')?.addEventListener('click',()=>this._showMoreCards());

      /* Mobile: summary header toggles the list */
      const summaryHeader = this.querySelector('.pc-summary-header');
      if (summaryHeader) {
        /* Add chevron icon to header */
        const icon = document.createElement('svg');
        icon.setAttribute('class','pc-summary-toggle-icon');
        icon.setAttribute('width','16'); icon.setAttribute('height','16');
        icon.setAttribute('viewBox','0 0 24 24');
        icon.setAttribute('fill','none'); icon.setAttribute('stroke','currentColor');
        icon.setAttribute('stroke-width','2.5');
        icon.innerHTML = '<polyline points="6 9 12 15 18 9"/>';
        summaryHeader.appendChild(icon);

        summaryHeader.addEventListener('click', () => {
          const summary = summaryHeader.closest('.pc-summary');
          summary?.classList.toggle('is-open');
        });
      }
    }

    /* ── PAYLOAD ── */
    _buildPayload() {
      const s = this._state;

      /* ── BASIC ── */
      const basic = { price_min: s.priceMin, price_max: s.priceMax };

      /* Battery — multi-select: map labels to enum values, send as battery_groups array
         Schema: battery_groups: BatteryGroup[]  do_9_dni | do_2_tygodni | do_miesiaca | solar */
      if (s.battery?.length) {
        const enumValues = s.battery
          .map(label => BATTERY_ENUM[label])
          .filter(Boolean);
        if (enumValues.length) basic.battery_groups = enumValues;
      }
      /* Case size — multi-select: send as array
         Schema: case_size_groups: CaseSizeGroup[]  small | medium | large */
      if (s.case_size_groups?.length) basic.case_size_groups = s.case_size_groups;
      if (s.payments !== null) basic.payments         = s.payments;
      if (s.calls_bluetooth)  basic.calls_bluetooth  = true;
      if (s.calls_lte)        basic.calls_lte        = true;
      if (s.multiband_gps)    basic.multiband_gps    = true;
      if (s.music)            basic.music            = true;

      /* ── ADVANCED ── */
      const advanced = {};

      /* Screen & water */
      if (s.water_resistance_atm !== null) advanced.water_resistance_atm = s.water_resistance_atm;
      if (s.display_type)   advanced.display_type  = s.display_type;
      if (s.screen_glass)   advanced.screen_glass  = s.screen_glass;
      if (s.flashlight !== null) advanced.flashlight = s.flashlight;

      /* Health & smart */
      if (s.ecg)            advanced.ecg            = true;
      if (s.hrv)            advanced.hrv            = true;
      if (s.morning_report) advanced.morning_report = true;
      if (s.evening_report) advanced.evening_report = true;
      if (s.coach_running)  advanced.coach_running  = true;
      if (s.coach_cycling)  advanced.coach_cycling  = true;
      if (s.coach_strength) advanced.coach_strength = true;
      if (s.coach_fitness)  advanced.coach_fitness  = true;
      if (s.recovery_time)  advanced.recovery_time  = true;
      if (s.maps !== null)  advanced.maps           = s.maps;

      /* ── SPORTS ──
         New schema supports two parallel formats:
         1. Flat arrays per group key:  advanced.bieganie = ["Bieganie", ...]
         2. activities[] array with primary/additional weight + values per group

         We send BOTH so the API can use whichever it prefers.

         The SportsWizard tracks insertion order in _usedGroups (a Set).
         Round 0 = primary, all subsequent rounds = additional.
      ── */
      const sportEntries = Object.entries(s.sports).filter(([, arr]) => arr?.length);

      if (sportEntries.length) {
        /* 1. Flat group arrays */
        sportEntries.forEach(([groupKey, arr]) => {
          advanced[groupKey] = arr;
        });

        /* 2. activities[] with weight
           The SportsWizard stores groups in insertion order inside this._sportsWizard._usedGroups.
           We reconstruct the order from that Set. If wizard isn't available, treat all as additional
           except the first group key in sportEntries. */
        const orderedGroups = this._sportsWizard
          ? Array.from(this._sportsWizard._usedGroups)
          : sportEntries.map(([k]) => k);

        advanced.activities = orderedGroups
          .filter(groupKey => s.sports[groupKey]?.length)
          .map((groupKey, idx) => ({
            primary: groupKey,
            values:  s.sports[groupKey],
            weight:  idx === 0 ? 'primary' : 'additional'
          }));
      }

      console.log('[pc] _buildPayload basic:', JSON.stringify(basic));
      console.log('[pc] _buildPayload advanced:', JSON.stringify(advanced));

      /* Client-side pagination: fetch all at once, we page locally */
      return {
        basic,
        ...(Object.keys(advanced).length ? { advanced } : {}),
        offset: 0,
        limit: 120
      };
    }

    /* ── FETCH ── */
    async _fetchRecommendations() {
      this._setLoading(true);
      const grid=this.querySelector('#pc-grid');
      grid.innerHTML=this._skeletonsHTML(CARDS_INITIAL);

      try {
        const payload=this._buildPayload();
        console.log('[pc] POST payload:', JSON.stringify(payload, null, 2));
        const res=await fetch(API_RECOMMENDATIONS,{
          method:'POST',headers:{'Content-Type':'application/json'},
          body:JSON.stringify(payload)
        });
        if(!res.ok)throw new Error(res.status);
        const data=await res.json();

        /* ── FIX: API returns { products:[{id,handle,score}], total, ... } ── */
        const items=data.products||data.results||data.recommendations||(Array.isArray(data)?data:[]);
        this._allHandles=items.map(i=>typeof i==='string'?i:i?.handle).filter(Boolean);

        const countEl=this.querySelector('#pc-count');
        if(countEl)countEl.innerHTML=`Znaleziono <strong>${this._allHandles.length}</strong> zegarków`;
      } catch(e) {
        console.error('[pc] Recommendations error:',e);
        this._allHandles=[];
      }

      this._setLoading(false);
      this._renderActivePills();

      if(!this._allHandles.length){
        grid.innerHTML=`<div class="pc-empty"><svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="8" y1="11" x2="14" y2="11"/></svg><p>Brak wyników</p><span>Spróbuj zmienić lub wyczyścić filtry</span></div>`;
        return;
      }

      this._shownCount=0;
      grid.innerHTML='';
      await this._renderCards(CARDS_INITIAL);
      this._updateLoadMore();
    }

    async _renderCards(count) {
      const grid=this.querySelector('#pc-grid');
      const start=this._shownCount;
      const slice=this._allHandles.slice(start,start+count);
      if(!slice.length)return;

      /* Add skeleton placeholders */
      const wrappers=slice.map((handle,i)=>{
        const w=document.createElement('div');w.className='pc-skeleton-wrap';
        w.innerHTML=this._skeletonHTML();grid.appendChild(w);
        return{w,handle,globalIdx:start+i};
      });

      await Promise.all(wrappers.map(({w,handle,globalIdx})=>
        this._fetchCardHTML(handle).then(html=>{
          if(!html){w.remove();return;}
          const wrap=document.createElement('div');wrap.className='pc-card-wrap';
          wrap.style.animationDelay=((globalIdx-start)*60)+'ms';
          if(globalIdx===0){
            const badge=document.createElement('span');
            badge.className='pc-best-badge';badge.textContent='Najlepsze dopasowanie';
            wrap.appendChild(badge);
          }
          wrap.insertAdjacentHTML('beforeend',html);
          /* Fix images injected dynamically — browser doesn't know container
             width yet so picks 1px srcset source. Force correct sizes. */
          /* Fix product images — theme generates broken sizes="calc(100vw / - 40px)"
             which makes browser pick the smallest srcset source on mobile.
             Target by class since movemore uses product-card__image. */
          wrap.querySelectorAll('img.product-card__image, img.product-card__image--primary').forEach(img => {
            img.loading = 'eager';
            if (img.dataset.src)    img.src    = img.dataset.src;
            if (img.dataset.srcset) img.srcset = img.dataset.srcset;
            /* Override broken calc() sizes with correct values:
               2 cols mobile = ~50vw, 3 cols tablet = ~33vw, 4 cols desktop = ~25vw */
            img.sizes = '(max-width: 767px) 50vw, (max-width: 1023px) 33vw, 25vw';
          });
          w.replaceWith(wrap);
        })
      ));

      this._shownCount+=slice.length;
    }

    _updateLoadMore() {
      const wrap=this.querySelector('#pc-load-more-wrap');
      if(wrap)wrap.style.display=this._shownCount<this._allHandles.length?'flex':'none';
    }

    async _showMoreCards() {
      await this._renderCards(CARDS_PER_PAGE);
      this._updateLoadMore();
    }

    async _fetchCardHTML(handle) {
      if(this._cardCache[handle])return this._cardCache[handle];
      try{
        const res=await fetch(`/products/${handle}?sections=${SECTION_RENDERER}`,{headers:{'X-Requested-With':'XMLHttpRequest'}});
        if(!res.ok)throw new Error(res.status);
        const data=await res.json();
        const html=data[SECTION_RENDERER]||'';
        this._cardCache[handle]=html;return html;
      }catch(e){console.warn(`[pc] Card "${handle}" failed:`,e);return null;}
    }

    _skeletonHTML(){
      return`<div class="pc-skeleton pc-skeleton-card"><div class="pc-skeleton-box" style="aspect-ratio:1;max-height:180px"></div><div style="padding:var(--spacing-3);display:flex;flex-direction:column;gap:var(--spacing-2)"><div class="pc-skeleton-box" style="height:10px;width:40%;border-radius:99px"></div><div class="pc-skeleton-box" style="height:14px;width:80%"></div><div class="pc-skeleton-box" style="height:11px;width:55%;border-radius:99px"></div></div></div>`;
    }
    _skeletonsHTML(n){return Array.from({length:n},()=>`<div class="pc-skeleton-wrap">${this._skeletonHTML()}</div>`).join('');}

    _setLoading(on){this.querySelector('#pc-loading')?.classList.toggle('is-visible',on);}

    /* ── ACTIVE PILLS ── */
    _renderActivePills() {
      const c=this.querySelector('#pc2-active-pills');if(!c)return;c.innerHTML='';
      const s=this._state,p=this._filterOptions?.basic?.ranges?.price||PRICE_DEFAULT;
      const pill=(label,onRemove)=>{
        const el=document.createElement('div');el.className='pc2-pill';
        el.innerHTML=`${label}<button type="button">✕</button>`;
        el.querySelector('button').addEventListener('click',onRemove);
        c.appendChild(el);
      };
      if(s.priceMin>p.min||s.priceMax<p.max)pill(`${s.priceMin.toLocaleString('pl-PL')}–${s.priceMax.toLocaleString('pl-PL')} zł`,()=>{s.priceMin=p.min;s.priceMax=p.max;this._resetPriceSlider(p);this._onFilterChange();});
      (s.battery||[]).forEach(b=>pill(`Bateria: ${b}`,()=>{s.battery=s.battery.filter(x=>x!==b);const btn=this.querySelector(`#pc2-battery .pc2-chip.is-active`);this.querySelector(`#pc2-battery`).querySelectorAll('.pc2-chip').forEach(c=>{if(c.dataset.value===b)c.classList.remove('is-active');});this._onFilterChange();}));
      (s.case_size_groups||[]).forEach(v=>{const lbl=(this._filterOptions?.basic?.case_size_groups||[]).find(g=>g.value===v)?.label||v;pill(`Rozmiar: ${lbl}`,()=>{s.case_size_groups=s.case_size_groups.filter(x=>x!==v);this.querySelector('#pc2-case-size').querySelectorAll('.pc2-chip').forEach(c=>{if(c.dataset.value===v)c.classList.remove('is-active');});this._onFilterChange();});});
      if(s.payments!==null)pill(`Płatności: ${s.payments?'Tak':'Nie'}`,()=>{s.payments=null;this._onFilterChange();});
      Object.entries({calls_bluetooth:'Rozmowy BT',calls_lte:'LTE',multiband_gps:'Multi-band GPS',music:'Muzyka'}).forEach(([k,l])=>{if(s[k])pill(l,()=>{s[k]=false;this._onFilterChange();});});
      if(s.water_resistance_atm!==null)pill(`Woda: ${s.water_resistance_atm} ATM`,()=>{s.water_resistance_atm=null;this._onFilterChange();});
      if(s.display_type)pill(`Ekran: ${s.display_type}`,()=>{s.display_type=null;this._onFilterChange();});
      if(s.screen_glass)pill(`Szkło: ${s.screen_glass}`,()=>{s.screen_glass=null;this._onFilterChange();});
      if(s.flashlight!==null)pill(`Latarka: ${s.flashlight?'Tak':'Nie'}`,()=>{s.flashlight=null;this._onFilterChange();});
      if(s.maps)pill('Mapy',()=>{s.maps=null;s.maps_bool=false;const cb=this.querySelector('#pc2-maps-toggle');if(cb)cb.checked=false;this._onFilterChange();});
      Object.entries({ecg:'EKG',hrv:'HRV',morning_report:'Poranny raport',evening_report:'Wieczorny raport',coach_running:'Coach: Bieganie',coach_cycling:'Coach: Rower',coach_strength:'Coach: Siłownia',coach_fitness:'Coach: Fitness',recovery_time:'Regeneracja'}).forEach(([k,l])=>{if(s[k])pill(l,()=>{s[k]=false;this._onFilterChange();});});
      Object.entries(s.sports).forEach(([group,acts])=>{ if(!acts?.length) return; const groupLabel=SPORT_GROUPS[group]||group; pill(groupLabel,()=>{delete s.sports[group];if(this._sportsWizard){this._sportsWizard._usedGroups.delete(group);this._sportsWizard._render();}this._onFilterChange();});});
    }

    _resetPriceSlider({min:_ignored,max}){
      const min = 0; /* always reset to 0 */
      const sMin=this.querySelector('#pc2-price-smin'),sMax=this.querySelector('#pc2-price-smax');
      const iMin=this.querySelector('#pc2-price-imin'),iMax=this.querySelector('#pc2-price-imax');
      const fill=this.querySelector('#pc2-range-fill'),disp=this.querySelector('#pc2-price-display');
      if(sMin){sMin.value=min;sMax.value=max;}if(iMin){iMin.value=min;iMax.value=max;}
      if(fill){fill.style.left='0%';fill.style.width='100%';}
      if(disp)disp.textContent=`${min.toLocaleString('pl-PL')} – ${max.toLocaleString('pl-PL')} zł`;
    }

    _onFilterChange(){
      clearTimeout(this._debounce);
      this._debounce=setTimeout(()=>{
        const results=this.querySelector('#pc-results-view');
        if(results&&!results.hasAttribute('hidden')){
          /* Rebuild answers from current state then refresh summary + products */
          (this._steps||[]).forEach(step=>this._recordAnswer(step));
          this._renderSummary();
          this._saveSession();
          this._fetchRecommendations();
        }
      },400);
    }
  }

  if(!window.customElements.get('garmin-konfigurator')){
    window.customElements.define('garmin-konfigurator',GarminKonfigurator);
  }
})();